import { Component, computed, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TableComponent } from '@custom-components/general/table/table.component';
import { FilledButtonComponent } from '@custom-components/general/button';
import { PageHeaderComponent } from '@custom-components/layout/page-header/page-header.component';
import { PageBodyComponent } from '@custom-components/layout/page-body/page-body.component';
import { BreadcrumbItem } from '@custom-components/general/breadcrumb/breadcrumb.component';
import {
  APP_PATHS,
  PAGINATION_DEFAULT,
  PARTNERSHIPS_TABLE_COLUMNS_CONFIG,
  XEMEX_OID,
} from '@core/constants';
import { getUserActivePartner, NewPartnership } from '@core/models';
import { PartnershipStore } from '@root/app/store/partnership.store';
import { AuthStore } from '@root/app/store/auth.store';
import { UserStore } from '@root/app/store/user.store';
import { CreatePartnershipDialogComponent } from '../../components/create-partnership-dialog/create-partnership-dialog.component';

@Component({
  selector: 'xemex-partnerships-page',
  imports: [
    TableComponent,
    FilledButtonComponent,
    PageHeaderComponent,
    PageBodyComponent,
    TranslateModule,
  ],
  templateUrl: './partnerships-page.component.html',
})
export class PartnershipsPageComponent {
  readonly partnershipStore = inject(PartnershipStore);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly authStore = inject(AuthStore);
  private readonly userStore = inject(UserStore);

  /** True when the user may create partnerships. */
  protected readonly canManage = computed(() => {
    const partnerOid = getUserActivePartner(this.userStore.user())?.partnerOid;
    const features =
      this.authStore
        .permissions()
        ?.filter(p => p.groupObjectId == partnerOid || p.groupObjectId == XEMEX_OID)
        .flatMap(p => p.roles.flatMap(role => role.features.flatMap(f => f.features)))
        .filter(f => f.featureEnabled)
        .map(f => f.featureName) ?? [];
    return features.includes('manage_partnerships');
  });

  protected readonly columns = PARTNERSHIPS_TABLE_COLUMNS_CONFIG;
  protected readonly breadcrumbs: BreadcrumbItem[] = [
    { label: this.translate.instant('sidebarMenu.home'), routerLink: '/' },
    { label: this.translate.instant('sidebarMenu.partnerships') },
  ];

  protected readonly tableData = computed(
    () => this.partnershipStore.data().partnerships as unknown as Record<string, unknown>[],
  );
  protected readonly totalCount = computed(() => this.partnershipStore.data().numberOfItems);

  protected readonly rowLink = (row: Record<string, unknown>) => [
    '/',
    APP_PATHS.Partnerships,
    row['partnershipOid'] as string,
  ];

  constructor() {
    this.partnershipStore.getPartnerships({
      limit: PAGINATION_DEFAULT.limit,
      offset: PAGINATION_DEFAULT.offset,
    });
  }

  protected onPageChange(evt: PageEvent): void {
    this.partnershipStore.getPartnerships({
      limit: PAGINATION_DEFAULT.limit,
      offset: evt.pageIndex,
    });
  }

  protected openCreateDialog(): void {
    this.dialog
      .open(CreatePartnershipDialogComponent, {
        width: '36rem',
        maxWidth: 'calc(100vw - 2rem)',
        maxHeight: 'calc(100vh - 2rem)',
      })
      .afterClosed()
      .subscribe((payload: NewPartnership | undefined) => {
        if (payload) {
          this.partnershipStore.createPartnership(payload);
        }
      });
  }
}
